import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  InputAdornment,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  TablePagination,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { LoadRoles, LoadUsers, NewUser, DeleteUser, UpdateUserStatus, branchesList } from '../BaseURL';
import { ErrorMessage, successMessage } from '../Message';

const Users = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [newUser, setNewUser] = useState({
    name: '',
    username: '',
    telephone: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    staff_id: '',
    branchId: ''
  });

  const [roleOptions, setRoleOptions]   = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);  // active branches only
  const [myBranch, setMyBranch] = useState(null);          // logged-in user's branch (locks the picker if not main)

  // Multi-tenancy is opt-in. Hide the Branch column + dialog card when the
  // facility runs only the main branch — keeps the UI clean for single-
  // facility deployments. Surfaces automatically as soon as a second active
  // branch exists.
  const usesBranches = branchOptions.length > 1;

  useEffect(() => {
    loadRoles();
    loadUsers();
    loadBranches();
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) setMyBranch(JSON.parse(raw)?.branch || null);
    } catch (_) {}
  }, []);

  const loadBranches = async () => {
    try {
      const { data } = await axios.get(branchesList, { params: { status: 'active' } });
      if (data?.success) setBranchOptions(data.results || []);
    } catch (_) { setBranchOptions([]); }
  };

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(LoadRoles);
      if (response.data.success) {
        setRoleOptions(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load roles',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(LoadUsers);
      if (response.data.success) {
        setUsers(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load users',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.tel.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.staff_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCreateUser = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewUser({
      name: '',
      username: '',
      telephone: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: '',
      staff_id: '',
      branchId: ''
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSaveUser = async () => {
    if (newUser.name && newUser.username && newUser.telephone &&
      newUser.password && newUser.confirmPassword && newUser.role && newUser.staff_id &&
      newUser.password === newUser.confirmPassword) {

      if (!newUser.telephone.match('^[0-9]{10}$')) {
        ErrorMessage('Error', 'Enter a valid phone number');
        return;
      }

      // Default branchId when the picker is hidden (single-facility setup)
      // or for sub-branch admins who see a locked dropdown. Falls back to
      // 1 (Main) for legacy logins whose userInfo predates multi-tenancy.
      if (!newUser.branchId) {
        newUser.branchId = myBranch?.id || 1;
      }

      try {
        const result = await Swal.fire({
          title: 'Confirm Action',
          text: "Are you sure you want to create this new user?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, create user'
        });

        if (result.isConfirmed) {
          setLoading(true);
          Swal.fire({
            title: 'Processing.......',
            text: 'Please wait',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          const response = await axios.post(NewUser, newUser);

          if (response.data.success) {
            Swal.close();
            successMessage('Success', response.data.message);
            await loadUsers();
            handleCloseDialog();
          }
        }
      } catch (error) {
        await Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to create user',
          icon: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditUser = (user) => {
    setNewUser({
      ...newUser,
      name: user.name,
      username: user.username,
      telephone: user.tel,
      email: user.email,
      role: user.role,
      staff_id: user.staff_id
    });
    handleCreateUser();
  };

  const handleDeleteUser = async (transCode) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Are you sure you want to delete user account",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        Swal.fire({
          title: 'Processing.......',
          text: 'Please wait',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const response = await axios.post(DeleteUser, { trans_code: transCode });

        if (response.data.success) {
          Swal.close();
          Swal.fire('Deleted!', 'User has been deleted.', 'success');
          await loadUsers();
        }
      } catch (error) {
        await Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete user',
          icon: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (transCode, field, currentValue) => {
    const fieldName = field === 'is_active' ? 'Active Status' : 'In Charge Status';
    const newStatus = currentValue === 1 ? 'Deactivate' : 'Activate';
    
    const result = await Swal.fire({
      title: 'Confirm Change',
      text: `Are you sure you want to change ${fieldName}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, change it!'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      Swal.fire({
        title: 'Processing.......',
        text: 'Please wait',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const newValue = currentValue === 1 ? 0 : 1;
      
      const response = await axios.post(UpdateUserStatus, {
        trans_code: transCode,
        field: field,
        value: newValue
      });

      if (response.data.success) {
        await loadUsers();
        Swal.close();
        successMessage('Success', `Status updated successfully`);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update status',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getRoleColor = (role) => {
    const colorMap = {
      'admin': 'error',
      'manager': 'warning',
      'staff': 'info',
      'user': 'success'
    };
    return colorMap[role?.toLowerCase()] || 'primary';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box>
        {/* Header Section */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.5
            }}>
              User Management
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage system users and their permissions
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            Create New User
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Users
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {users.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Active Roles
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {roleOptions.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Search Results
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {filteredUsers.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Search by name, username, role, staff ID, or phone..."
            variant="outlined"
            size="medium"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: 2
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  User
                </TableCell>

                <TableCell sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Contact
                </TableCell>

                <TableCell sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Role
                </TableCell>

                {usesBranches && (
                  <TableCell sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    py: 2.5
                  }}>
                    Branch
                  </TableCell>
                )}

                <TableCell sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Staff ID
                </TableCell>

                <TableCell align="center" sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Is Active
                </TableCell>

                <TableCell align="center" sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  In Charge
                </TableCell>

                <TableCell align="center" sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      '&:last-child td': { border: 0 }
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'primary.main',
                            width: 40,
                            height: 40,
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        >
                          {getInitials(user.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.25
                          }}>
                            {user.name}
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: 'text.secondary',
                            fontSize: '0.813rem'
                          }}>
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{
                        color: 'text.primary',
                        mb: 0.25,
                        fontWeight: 500
                      }}>
                        {user.tel}
                      </Typography>
                      {user.email && (
                        <Typography variant="body2" sx={{
                          color: 'text.secondary',
                          fontSize: '0.813rem'
                        }}>
                          {user.email}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={user.role}
                        color={getRoleColor(user.role)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 1.5,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>

                    {usesBranches && (
                      <TableCell sx={{ py: 2 }}>
                        {user.branch_code ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Chip
                              label={user.branch_code}
                              size="small"
                              sx={{
                                height: 22, fontSize: '0.7rem', fontWeight: 700, borderRadius: 1,
                                bgcolor: user.branch_id === 1 ? 'rgba(14,146,106,0.10)' : 'rgba(26,107,138,0.10)',
                                color:   user.branch_id === 1 ? '#0a7554' : '#1a6b8a',
                                border: `1px solid ${user.branch_id === 1 ? 'rgba(14,146,106,0.30)' : 'rgba(26,107,138,0.30)'}`,
                              }}
                            />
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                              {user.branch_name}
                            </Typography>
                            {user.branch_status && user.branch_status !== 'active' && (
                              <Chip
                                label={user.branch_status}
                                size="small"
                                sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(234,179,8,0.10)', color: '#a16207' }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.78rem', fontStyle: 'italic' }}>
                            unassigned
                          </Typography>
                        )}
                      </TableCell>
                    )}

                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{
                        color: 'text.primary',
                        fontWeight: 500
                      }}>
                        {user.staff_id}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Switch
                        checked={user.is_active === 1}
                        onChange={() => handleStatusChange(user.trans_code, 'is_active', user.is_active)}
                        color="success"
                        size="small"
                        disabled={loading || user.role === 'System Administrator'}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Switch
                        checked={user.is_staff === 1}
                        onChange={() => handleStatusChange(user.trans_code, 'is_staff', user.is_staff)}
                        color="primary"
                        size="small"
                        disabled={loading || user.role === 'System Administrator'}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            onClick={() => handleEditUser(user)}
                            size="small"
                            disabled={loading}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.50'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            onClick={() => handleDeleteUser(user.trans_code)}
                            size="small"
                            disabled={loading}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.50'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={usesBranches ? 8 : 7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          />
        </TableContainer>
      </Box>

      {/* Create User Dialog */}
      <Dialog
        open={openDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 8
          }
        }}
      >
        {/* Dialog Header */}
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #41a386 0%, #41a386 100%)',
          color: 'white',
          py: 3,
          px: 4
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Create New User
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Fill in the information below to create a new system user
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseDialog}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 4, backgroundColor: 'grey.50' }}>
          <Grid container spacing={4}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.100',
                  backgroundColor: 'white'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.100'
                }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 32, 
                    backgroundColor: 'primary.main', 
                    borderRadius: 1 
                  }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Basic Information
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Full Name"
                      variant="outlined"
                      value={newUser.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="e.g., John Doe"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Staff ID"
                      variant="outlined"
                      value={newUser.staff_id}
                      onChange={(e) => handleInputChange('staff_id', e.target.value)}
                      placeholder="e.g., EMP001"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Telephone Number"
                      type="number"
                      variant="outlined"
                      value={newUser.telephone}
                      onChange={(e) => handleInputChange('telephone', e.target.value)}
                      placeholder="e.g., 0241234567"
                      helperText="Enter 10-digit phone number"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address (Optional)"
                      type="email"
                      variant="outlined"
                      value={newUser.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="e.g., john.doe@hospital.com"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Login Credentials Section */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'secondary.100',
                  backgroundColor: 'white'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'secondary.100'
                }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 32, 
                    backgroundColor: 'secondary.main', 
                    borderRadius: 1 
                  }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                    Login Credentials
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Username"
                      variant="outlined"
                      value={newUser.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      placeholder="e.g., johndoe"
                      helperText="Username must be unique"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={newUser.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter strong password"
                      helperText="Minimum 8 characters recommended"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={newUser.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      placeholder="Re-enter password"
                      error={newUser.confirmPassword && newUser.password !== newUser.confirmPassword}
                      helperText={
                        newUser.confirmPassword && newUser.password !== newUser.confirmPassword
                          ? "Passwords don't match"
                          : "Must match password above"
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              size="small"
                            >
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Role Assignment Section */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'success.100',
                  backgroundColor: 'white'
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 1,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'success.100'
                }}>
                  <Box sx={{
                    width: 8,
                    height: 32,
                    backgroundColor: 'success.main',
                    borderRadius: 1
                  }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      Role Assignment
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      Controls what the user can see and do across the system.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel required>Select User Role</InputLabel>
                    <Select
                      required
                      value={newUser.role}
                      label="Select User Role"
                      onChange={(e) => handleInputChange('role', e.target.value)}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: 'white'
                      }}
                    >
                      {roleOptions.map((role) => (
                        <MenuItem key={role.role} value={role.role}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
                            <Chip
                              label={role.role}
                              size="small"
                              color={getRoleColor(role.role)}
                              sx={{
                                textTransform: 'capitalize',
                                fontWeight: 600,
                                minWidth: 80
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {role.description || `User with ${role.role} privileges`}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Paper>
            </Grid>

            {/* Branch Assignment Section — only shown when the facility
                runs more than one branch. Main-branch admins can assign to
                any active branch; sub-branch admins are pinned to their own. */}
            {usesBranches && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'info.100',
                  backgroundColor: 'white'
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 1,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'info.100'
                }}>
                  <Box sx={{
                    width: 8,
                    height: 32,
                    backgroundColor: 'info.main',
                    borderRadius: 1
                  }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                      Branch Assignment
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      {(myBranch && !myBranch.isMain)
                        ? 'Sub-branch admins can only register users into their own branch.'
                        : 'Pick which branch this user belongs to. They will only see that branch\'s patients and operations.'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel required>Select Branch</InputLabel>
                    <Select
                      required
                      value={newUser.branchId || (myBranch?.id || '')}
                      label="Select Branch"
                      onChange={(e) => handleInputChange('branchId', e.target.value)}
                      disabled={!!(myBranch && !myBranch.isMain)}
                      sx={{ borderRadius: 2, backgroundColor: 'white' }}
                    >
                      {(myBranch && !myBranch.isMain
                        ? branchOptions.filter(b => b.id === myBranch.id)
                        : branchOptions
                      ).map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
                            <Chip
                              label={b.code}
                              size="small"
                              sx={{
                                fontWeight: 700, minWidth: 80, textTransform: 'uppercase',
                                bgcolor: b.id === 1 ? 'rgba(14,146,106,0.12)' : 'rgba(26,107,138,0.12)',
                                color:   b.id === 1 ? '#0a7554' : '#1a6b8a',
                                border: `1px solid ${b.id === 1 ? 'rgba(14,146,106,0.30)' : 'rgba(26,107,138,0.30)'}`,
                              }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {b.name}{b.id === 1 ? ' · HQ' : ''}
                              </Typography>
                              {b.location && (
                                <Typography variant="caption" color="text.secondary">
                                  {b.location}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Paper>
            </Grid>
            )}
          </Grid>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 3, backgroundColor: 'white', gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            size="large"
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 4,
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            size="large"
            disabled={
              loading ||
              !newUser.name ||
              !newUser.username ||
              !newUser.telephone ||
              !newUser.password ||
              !newUser.confirmPassword ||
              !newUser.role ||
              !newUser.staff_id ||
              newUser.password !== newUser.confirmPassword
            }
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 4,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #41a386 0%, #41a386 100%)',
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6
              }
            }}
          >
            Create User Account
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Users;